import React, {CSSProperties, useEffect, useRef, useState} from "react";
import {kebabCase} from "../../utils/string/change-case";

type SassCompilerType = {
    compile: (scss: string, cb: (result: {status?: number, message?: string, text?: string}) => void) => void
}

const loadSass = new Promise<SassCompilerType>((resolve, reject) => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        // @ts-ignore
        if (typeof window.Sass === 'undefined' || typeof window.Sass.compile !== 'function') {
            const sassJSScript = document.createElement('script');
            sassJSScript.setAttribute("type", "text/javascript");
            sassJSScript.setAttribute("src", "https://cdnjs.cloudflare.com/ajax/libs/sass.js/0.9.12/sass.sync.min.js");
            sassJSScript.onload = () => {
                // @ts-ignore
                resolve(window.Sass);
            };
            document.head.appendChild(sassJSScript);
        } else {
            // @ts-ignore
            resolve(window.Sass);
        }
    }
});

type StyleInjectorPropsType = {
    id?: string,
    style: string
}

export default (props: StyleInjectorPropsType) => {
    const ref = useRef(null);
    const [style, setStyle] = useState('');
    useEffect(() => {
        let canceled = false;
        loadSass.then((Sass) => {
            Sass.compile(props.id ? "." + props.id + "{" + (props.style || '') + "}" : props.style, (result) => {
                if (canceled) return;
                if (result.message) {
                    console.error(result.message);
                }
                if (ref.current) {
                    ref.current.innerHTML = result.text || '';
                }
                setStyle(result.text || '');
            });
        });
        return () => {
            canceled = true;
        }
    }, [props.id, props.style]);
    useEffect(() => {
        if (ref.current) {
            ref.current.innerHTML = style;
        }
    }, [style]);

    return <style ref={ref} type="text/css" />;
}

type CSSPropType = {[K in keyof CSSProperties]: CSSProperties[K]|{[key: string]: CSSProperties[K]}};

export type StyleType = {[key: string]: CSSPropType};
export type VarType = {[key: string]: string|number|null|{[key: string]: string|number|null}};

const aliasSymbol = Symbol("$alias");

function hashCode(s: string) {
    let h = 0, l = s.length, i = 0;
    if ( l > 0 )
        while (i < l)
            h = (h << 5) - h + s.charCodeAt(i++) | 0;
    return String(h).split('').map((a) => a === '-' ? 'x' : String.fromCharCode(Number(a) + 97)).join('');
}

export const create = <T = StyleType>(style: T): T => {
    return Object.keys(style).reduce((style, className) => {
        style[className][aliasSymbol] = hashCode(`${className}-${new Date().getTime()}`);
        return style;
    }, style);
}

export const defineVars = <T = VarType>(vars: T): T => {
    return Object.keys(vars).reduce((vars, varName) => {
        vars[varName][aliasSymbol] = hashCode(`${varName}-${new Date().getTime()}`);
        return vars;
    }, vars);
}

export const props = (...styles: CSSPropType[]) => {
    return {
        className: styles.map((style) => style[aliasSymbol as unknown as string]).join(" ")
    }
}

const compileStyle = (style: StyleType) => {
    return Object.entries(style).reduce((compiled, [className, classProps]) => {
        return compiled + Object.entries(Object.entries(classProps).reduce((compiled, [styleName, value]) => {
            if ((styleName as unknown as symbol) === aliasSymbol) {
                return compiled;
            }
            className = classProps[aliasSymbol] ?? className;
            if (value && typeof value === 'object') {
                return Object.entries(value).reduce((compiled, [state, value]) => {
                    state = state.trim();
                    if (state === "default") {
                        const key = `.${className}`;
                        compiled[key] = compiled[key] ?? {};
                        compiled[key][`${kebabCase(styleName)}`] = value;
                    } else if (state.trim().slice(0, 1) === ":") {
                        const key = `.${className}${state}`;
                        compiled[key] = compiled[key] ?? {};
                        compiled[key][`${kebabCase(styleName)}`] = value;
                    } else if (state.trim().slice(0, 1) === "@") {
                        const key = `${state}{.${className}`;
                        compiled[key] = compiled[key] ?? {};
                        compiled[key][`${kebabCase(styleName)}`] = value;
                    }
                    return compiled;
                }, compiled);
            }
            const key = `.${className}`;
            compiled[key] = compiled[key] ?? {};
            compiled[key][`${kebabCase(styleName)}`] = value;
            return compiled;
        }, {})).sort((a, b) => a[0].localeCompare(b[0])).reduce((compiled, [className, values]) => {
            return compiled + `\n${className}{${Object.entries(values).reduce((compiled, [styleName, value]) => {
                if (value !== null) {
                    return compiled + `${styleName}: ${value};`;
                }
                return compiled;
            }, '')}${'}'.repeat(className.split('{').length)}`;
        }, '');
    }, '');
}

export const injectStyles = (style: StyleType) => {
    const compiledStyle = compileStyle(style);
    return () => <StyleInjector style={compiledStyle} />
}

export const inject = (style: StyleType) => {
    const Injected = injectStyles(style);
    return <T,>(Component: T): T => {
        // @ts-ignore
        return React.forwardRef((props, ref) => {
            // @ts-ignore
            const roasted = <Component ref={ref} {...props} />;
            return <>
                <Injected />
                {roasted}
            </>
        });
    }
}